/**
 * Supplier Company Merge API — CR-5901
 *
 * Wraps the backend endpoints under /core/admin/supplier-merges/ that
 * power the Admin Setup → Suppliers merge UI.
 */
import api, { type AxiosResponse, type PaginatedResponse } from './client';

/** Counts for one data area, in the preview and in a merge's history row. */
export interface SupplierMergeTableResult {
  /** Rows re-pointed from the duplicate to the kept supplier */
  moved: number;
  /** Rows folded into a row the kept supplier already had (customer relationships) */
  combined: number;
  /** Rows the kept supplier already holds; the duplicate's copy stays on the archived record */
  already_on_kept: number;
}

/** What happens to one of the duplicate's customer relationships. */
export interface SupplierMergeRelationshipChange {
  customer_id: number | null;
  customer_name: string;
  /** `combined` when the kept supplier already serves that customer site */
  action: 'moved' | 'combined';
  /** Supplier codes the kept supplier gains for this customer */
  codes_added: string[];
  /** Codes the kept supplier already had, kept once */
  codes_already: string[];
}

/** History each supplier holds within the caller's view, to pick the right one to keep. */
export interface SupplierMergeHistoryCounts {
  orders: number;
  ncrs: number;
  sites: number;
}

export interface SupplierMergePreview {
  results: Record<string, SupplierMergeTableResult>;
  totals: SupplierMergeTableResult;
  relationships: SupplierMergeRelationshipChange[];
  sides: { correct: SupplierMergeHistoryCounts; incorrect: SupplierMergeHistoryCounts };
}

export interface SupplierMergeLog {
  id: number;
  correct_company_id: number | null;
  correct_company_name: string;
  incorrect_company_id: number | null;
  incorrect_company_name: string;
  performed_by: number | null;
  performed_by_name: string;
  performed_at: string;
  status: 'preview' | 'success' | 'failed';
  status_display: string;
  results: Record<string, SupplierMergeTableResult>;
  totals: SupplierMergeTableResult;
  relationship_changes: SupplierMergeRelationshipChange[];
  error_message: string;
}

export interface ExecuteMergePayload {
  correct_company_id: number;
  incorrect_company_id: number;
}

export interface SelectableSupplier {
  id: number;
  name: string;
  is_active: boolean;
}

const BASE = '/core/admin/supplier-merges/';

export const companyMergeAPI = {
  listHistory: (): Promise<AxiosResponse<PaginatedResponse<SupplierMergeLog>>> =>
    api.get(BASE),

  selectable: (): Promise<AxiosResponse<{ results: SelectableSupplier[] }>> =>
    api.get(`${BASE}selectable/`),

  preview: (payload: ExecuteMergePayload): Promise<AxiosResponse<SupplierMergePreview>> =>
    api.post(`${BASE}preview/`, payload),

  execute: (payload: ExecuteMergePayload): Promise<AxiosResponse<SupplierMergeLog>> =>
    api.post(BASE, payload),
};
